import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import locationApi from "../utilities/locationApi";
import { toutesLesQuetes } from "../utilities/locationService";
import { enCfa, useValeurTip } from "../utilities/valeurTip";

/* Couleurs de la charte (src/theme/tribeTheme.ts), mêmes clés qu'avant. */
const C = {
  bg: "var(--c-bg)",
  card: "var(--c-surface)",
  border: "var(--c-border)",
  accent: "#1F1E1B",
  text: "#1F1E1B",
  textSub: "#57534A",
  textMuted: "#57534A",
} as const;

interface Stat {
  label: string;
  value: string | number;
}

interface TileProps {
  title: string;
  subtitle: string;
  stats: Stat[];
  loading: boolean;
  onClick: () => void;
}

function Tile({ title, subtitle, stats, loading, onClick }: TileProps) {
  return (
    <Box
      onClick={onClick}
      sx={{
        flex: "1 1 280px",
        background: C.card,
        border: "1.5px solid #1F1E1B",
        borderRadius: "8px",
        p: "32px",
        cursor: "pointer",
        transition: "border-color 0.2s, box-shadow 0.2s",
        display: "flex",
        flexDirection: "column",
        "&:hover": {
          borderColor: "rgba(31,30,27,0.4)",
          boxShadow: "none",
        },
      }}
    >
      <Typography
        sx={{
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: "20px",
          color: C.text,
          mb: "6px",
        }}
      >
        {title}
      </Typography>
      <Typography sx={{ fontSize: "13px", color: C.textSub, mb: "32px" }}>
        {subtitle}
      </Typography>

      <Box sx={{ display: "flex", flexDirection: "column", gap: "20px", flex: 1 }}>
        {stats.map((s) => (
          <Box key={s.label}>
            <Typography
              sx={{
                fontSize: "10px",
                color: C.textMuted,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                mb: "4px",
              }}
            >
              {s.label}
            </Typography>
            <Typography
              sx={{
                fontFamily: "var(--font-display)",
                fontWeight: 700,
                fontSize: "26px",
                color: loading ? C.textMuted : C.text,
                lineHeight: 1,
              }}
            >
              {loading ? "—" : s.value}
            </Typography>
          </Box>
        ))}
      </Box>

      <Typography
        sx={{
          mt: "32px",
          fontSize: "13px",
          fontWeight: 600,
          color: C.accent,
          letterSpacing: "0.02em",
        }}
      >
        Accéder →
      </Typography>
    </Box>
  );
}

/* Question 15 : l'accueil et le tableau de bord ne font plus qu'une page.
   Ces tuiles sont rendues en tête du tableau de bord (integre). */
export default function Home({ integre = false }: { integre?: boolean }) {
  const navigate = useNavigate();

  const [opsStats, setOpsStats] = useState({ pending: 0, activeQuests: 0, collectors: 0 });
  const [opsLoading, setOpsLoading] = useState(true);

  const [dataStats, setDataStats] = useState({ totalTribe: 0 });
  const [dataLoading, setDataLoading] = useState(true);

  const [finStats, setFinStats] = useState({ devis: 0, budgetTips: 0, actives: 0 });
  const valeurTip = useValeurTip();
  const [finLoading, setFinLoading] = useState(true);

  useEffect(() => {
    // Compteurs du serveur, mêmes définitions que le tableau de bord (Z1-16) :
    // statistiques des POI, agrégat par Spotter, liste des quêtes.
    const il_y_a_30_jours = Date.now() - 30 * 86400000;
    Promise.allSettled([
      locationApi.get("/location/statistiques"),
      locationApi.get("/location/statistiques/spotters"),
      toutesLesQuetes(),
    ]).then(([st, spotters, quetes]) => {
      const s = st.status === "fulfilled" ? st.value.data : null;
      const liste: any[] = quetes.status === "fulfilled" ? quetes.value : [];
      setOpsStats({
        pending: Number(s?.tribe?.enAttente ?? 0),
        // Quêtes publiées (l'endpoint /quest/active est celui des Spotters, Z1-8).
        activeQuests: liste.filter((q) => q.statut === "publiee").length,
        // Spotters ayant collecté au moins un lieu ces 30 derniers jours (Z1-9).
        collectors: spotters.status === "fulfilled" && Array.isArray(spotters.value.data)
          ? spotters.value.data.filter((x: any) => x.dernier && new Date(x.dernier).getTime() >= il_y_a_30_jours).length
          : 0,
      });
      setDataStats({ totalTribe: Number(s?.tribe?.total ?? 0) });
      setFinStats({
        devis: liste.filter((q) => q.statutPaiement === "devis_envoye").length,
        // Tips des quêtes multipliés par la valeur officielle du Tip (D1).
        budgetTips: liste.reduce((sum, q) => sum + (q.tokensBase ?? 0) * (q.collectesRealisees ?? 0), 0),
        actives: liste.filter((q) => q.statut === "publiee").length,
      });
      setOpsLoading(false);
      setDataLoading(false);
      setFinLoading(false);
    });
  }, []);

  const fmt = (n: number) => n.toLocaleString("fr-FR");
  const fmtCFA = (n: number) => fmt(n) + " F CFA";

  return (
    <Box
      sx={{
        minHeight: "100%",
        background: C.bg,
        p: integre ? { xs: "16px", md: "24px 32px" } : { xs: "24px", md: "48px 56px" },
      }}
    >
      {!integre && (<>
      <Typography
        sx={{
          fontFamily: "var(--font-display)",
          fontWeight: 700,
          fontSize: "28px",
          color: C.text,
          mb: "8px",
        }}
      >
        Accueil
      </Typography>
      <Typography sx={{ fontSize: "14px", color: C.textSub, mb: "48px" }}>
        Vue d'ensemble du backoffice TRIBE — accédez à vos outils.
      </Typography>
      </>)}

      <Box sx={{ display: "flex", gap: "24px", flexWrap: "wrap", alignItems: "stretch" }}>
        <Tile
          title="Opérations"
          subtitle="Suivi des collectes terrain et des missions"
          loading={opsLoading}
          onClick={() => navigate("/a-traiter/lieux")}
          stats={[
            { label: "Lieux en attente de validation", value: fmt(opsStats.pending) },
            { label: "Quêtes actives", value: fmt(opsStats.activeQuests) },
            { label: "Spotters actifs (30 j)", value: fmt(opsStats.collectors) },
          ]}
        />
        <Tile
          title="Données"
          subtitle="Analyse comparative des données cartographiques"
          loading={dataLoading}
          onClick={() => navigate("/analyses/comparaison-des-sources")}
          stats={[
            // « ×54 » et le nombre de zones étaient écrits en dur : retirés
            // tant qu'ils ne sont pas calculés (question 15).
            { label: "Lieux Tribe", value: fmt(dataStats.totalTribe) },
          ]}
        />
        <Tile
          title="Finance"
          subtitle="Ventes et budget des quêtes"
          loading={finLoading}
          onClick={() => navigate("/argent/ventes")}
          stats={[
            { label: "Devis en attente", value: fmt(finStats.devis) },
            { label: "Budget Spotters", value: (() => { const c = enCfa(finStats.budgetTips, valeurTip); return c == null ? "—" : fmtCFA(c); })() },
            { label: "Quêtes actives", value: fmt(finStats.actives) },
          ]}
        />
      </Box>
    </Box>
  );
}
